import { ofType } from 'redux-observable';
import { switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import type { RootEpic } from '@/store/types';
import { storageActions } from '@/pages/SettingPage/StorageManagement/redux/storageSlice';
import { uploadFileActions } from '@/store/uploadFiles/uploadFileSlice';
import { folderUploadActions } from '@/store/folderUpload/folderUploadSlice';

/**
 * UPLOAD INTERCEPT EPIC
 * 
 * Responsibility: Listen to proceedUpload action and dispatch actual upload actions
 * 
 * Flow:
 * 1. storageCapacityEpic validates capacity
 * 2. If OK → dispatch proceedUpload with uploadContext
 * 3. This epic intercepts proceedUpload
 * 4. Dispatches appropriate upload action based on uploadType
 *    - 'file' → uploadFileActions.checkFileExistsRequest
 *    - 'folder' → folderUploadActions.startFolderUpload
 */
export const uploadInterceptEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.proceedUpload.type),
    switchMap((action: any) => {
      const { uploadContext } = action.payload;
      const { uploadType, uploadPayload } = uploadContext;

      if (uploadType === 'file') {
        // Calculate total files and set before dispatching upload
        const totalFiles = uploadPayload.files?.length || 0;
        const files = uploadPayload.files || [];
        const folderId = uploadPayload.parentFolderId || '';
        const isOrganization = uploadPayload.isOrganization;
        const projectId = uploadPayload.project;
        const companyId = uploadPayload.companyId;
        
        // Create queueFiles with uniqueId similar to folderUploadEpic
        const queueFiles = files.map((file: File) => {
          // Generate uniqueId only once
          const uniqueId = `${file.name}_${folderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          // Attach uniqueId and folderId to file object IMMEDIATELY
          Object.defineProperty(file, "__uniqueId", {
            value: uniqueId,
            writable: false,
            enumerable: false,
            configurable: true,
          });
          
          Object.defineProperty(file, "__parentFolderId", {
            value: folderId,
            writable: false,
            enumerable: false,
            configurable: true,
          });
          
          return {
            file,
            folderId,
            status: "pending" as const,
            progress: 0,
            fileName: `${file.name}_${folderId}_${file.size}`, // Unique identifier to handle duplicate names
            uniqueId, // Same uniqueId as attached to file object
            isOrganization,
            projectId,
            companyId,
          };
        });
        
        console.log('📊 Created queueFiles:', queueFiles.length);
        
        return from([
          folderUploadActions.addFilesToQueue(queueFiles),
        ]);
      } else if (uploadType === 'folder') {
        // Calculate total files for folder upload
        const totalFiles = uploadPayload.files?.length || 0;
        console.log('📂 Dispatching startFolderUpload with', totalFiles, 'files');
        
        return from([
          folderUploadActions.startFolderUpload(uploadPayload),
        ]);
      }

      // Unknown upload type - do nothing
      console.warn('⚠️ Unknown upload type:', uploadType);
      return from([{ type: 'UPLOAD_INTERCEPT_NO_ACTION' }]);
    })
  );

export const uploadInterceptEpics = [uploadInterceptEpic$];
